import React from 'react'
import { Link, Head } from '@inertiajs/react'
import OnboardingShell from '@/components/onboarding/OnboardingShell'
import { HelpTooltip } from '@/components/HelpTooltip'
import { useBrand } from '@/hooks/useBrand'
import { ONBOARDING_COMPLETE_STEP } from '@/lib/onboarding'
import {
  CheckCircle2,
  SlidersHorizontal,
  CalendarDays,
  Sparkles,
  ShieldCheck,
  ArrowRight,
} from 'lucide-react'
import type { SharedProps } from '@/types/shared'

/**
 * Step 4 — the explicit end of the onboarding journey. Sets expectations
 * (preview prices are being prepared; nothing reaches Eviivo before Go Live)
 * and hands off down one of two deliberate exits:
 *   1. "Set up pricing rules now"  → the property's Pricing Rules rooms page
 *   2. "I'm done for now"          → the Prices calendar (warm-up card + Go
 *      Live banner already live there)
 */
export default function Finish({ currentUser, property, currentAccount }: SharedProps) {
  const brand = useBrand()
  const pricingRulesHref = `${property.basePath}/pricing-rules/rooms-setup`
  const calendarHref = `${property.basePath}/calendar/prices`

  return (
    <OnboardingShell
      currentStep={ONBOARDING_COMPLETE_STEP}
      contextChip={{ label: 'Setting up', value: property.name, kind: 'property' }}
      signedInAs={currentUser.email}
      portfolioPath={currentAccount?.basePath ?? null}
      exitHref={calendarHref}
    >
      <Head title="Finish setup" />
      <div className="mx-auto max-w-2xl space-y-6">
        {/* Success header */}
        <div className="animate-slide-in text-center" style={{ animationDelay: '0ms' }}>
          <div
            className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/25"
            style={{ animation: 'finishPop 0.4s ease-out' }}
          >
            <CheckCircle2 className="h-7 w-7 text-white" />
          </div>
          <div className="mt-4 flex items-center justify-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-foreground">
              {property.name} is connected
            </h1>
            <HelpTooltip content={`${brand} is now reading this property's structure and bookings from your PMS and preparing preview prices. Prices stay preview-only until someone presses Go Live on the calendar — nothing is sent back to your PMS before that.`} />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Setup is done. Here&apos;s what happens next — and where to go from here.
          </p>
        </div>

        {/* What happens next */}
        <div className="animate-slide-in" style={{ animationDelay: '80ms' }}>
          <div className="rounded-xl border border-border/60 bg-white shadow-sm">
            <div className="border-b border-border/40 px-5 py-4">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-foreground">What happens next</h2>
                <HelpTooltip content="Preview prices usually appear within a few minutes, once the first computation finishes. The Prices calendar shows a warm-up progress card while the property settles in." />
              </div>
            </div>
            <ul className="space-y-3 px-5 py-4">
              <li className="flex items-start gap-3">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <p className="text-sm text-foreground/80">
                  {brand} is preparing <span className="font-medium text-foreground">preview prices</span>{' '}
                  in the background — they&apos;ll appear on the Prices calendar shortly.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <p className="text-sm text-foreground/80">
                  <span className="font-medium text-foreground">Nothing is sent to your PMS</span> until
                  someone presses Go&nbsp;Live. It keeps its current prices for now.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <SlidersHorizontal className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <p className="text-sm text-foreground/80">
                  Prices and rules are managed under{' '}
                  <span className="font-medium text-foreground">Pricing Rules</span>.
                </p>
              </li>
            </ul>
          </div>
        </div>

        {/* The two exits */}
        <div className="animate-slide-in grid gap-4 sm:grid-cols-2" style={{ animationDelay: '160ms' }}>
          {/* Exit 1: pricing operator */}
          <div className="flex flex-col rounded-xl border border-primary/30 bg-white p-5 shadow-sm ring-1 ring-primary/10">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <SlidersHorizontal className="h-5 w-5 text-primary" />
            </div>
            <h3 className="mt-3 text-sm font-semibold text-foreground">Set up pricing rules now</h3>
            <p className="mt-1 flex-1 text-xs text-muted-foreground">
              Best if you look after pricing yourself — finish room prices, then add seasons,
              weekday patterns and price limits.
            </p>
            <Link
              href={pricingRulesHref}
              className="mt-4 inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground outline-none transition-colors hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              Open Pricing Rules
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          {/* Exit 2: hand it over */}
          <div className="flex flex-col rounded-xl border border-border/60 bg-white p-5 shadow-sm">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
              <CalendarDays className="h-5 w-5 text-muted-foreground" />
            </div>
            <h3 className="mt-3 text-sm font-semibold text-foreground">I&apos;m done for now</h3>
            <p className="mt-1 flex-1 text-xs text-muted-foreground">
              Someone else will tune pricing later. Watch preview prices arrive on the calendar and
              go live whenever you&apos;re ready.
            </p>
            <Link
              href={calendarHref}
              className="mt-4 inline-flex items-center justify-center gap-1.5 rounded-lg border border-border bg-white px-4 py-2 text-sm font-medium text-foreground outline-none transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              View the price calendar
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes finishPop {
          from { opacity: 0; transform: scale(0.6); }
          60% { transform: scale(1.08); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </OnboardingShell>
  )
}
